import { Link } from "react-router-dom";
import { Clock, Zap } from "lucide-react";
import type { BlogPost } from "@/data/blogPosts";
import { getToolIcon } from "@/utils/toolIcons";

interface ToolListSectionProps {
  articles: BlogPost[];
  ctaLabel?: string;
  ctaLink?: string;
}

const getToolName = (post: BlogPost): string => {
  if (post.tools && post.tools.length > 0) return post.tools[0];
  return post.title.split(':')[0].trim();
};

const ToolListSection = ({ articles, ctaLabel, ctaLink }: ToolListSectionProps) => {
  if (articles.length === 0) return null;

  return (
    <section className="py-24 md:py-32 px-4 bg-background">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-center mb-20">
          Las herramientas que puedes usar
        </h2>

        <div className="space-y-6">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="group block rounded-2xl border border-border/20 bg-secondary/10 hover:bg-secondary/20 p-6 md:p-8 transition-all duration-500"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                {/* Emoji + Name */}
                <div className="flex items-center gap-3 md:min-w-[220px] shrink-0">
                  {(() => {
                    const IconComponent = getToolIcon(article.emoji);
                    return <IconComponent className="h-8 w-8 text-foreground shrink-0" />;
                  })()}
                  <span className="text-xl md:text-2xl font-medium text-foreground group-hover:text-primary transition-colors">
                    {getToolName(article)}
                  </span>
                </div>

                {/* Description */}
                <div className="flex-1 space-y-3">
                  <p className="text-base md:text-lg font-light text-muted-foreground leading-relaxed line-clamp-2">
                    {article.businessSolution || article.description}
                  </p>

                  {/* Metadata row */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    {article.avgImplementationTime && (
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        {article.avgImplementationTime}
                      </span>
                    )}
                    {article.diyDifficulty && (
                      <span className="flex items-center gap-1.5">
                        <Zap className="h-3.5 w-3.5" />
                        {article.diyDifficulty}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <span className="hidden md:block text-xl text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 self-center">
                  →
                </span>
              </div>
            </Link>
          ))}
        </div>

        {ctaLabel && ctaLink && (
          <div className="text-center pt-16">
            <Link
              to={ctaLink}
              className="group inline-flex items-center gap-3 text-lg md:text-xl font-light text-foreground hover:text-primary transition-colors"
            >
              <span>{ctaLabel}</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ToolListSection;
